import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { CheckCircle2, ExternalLink, UserRound, PanelLeftClose, PanelLeftOpen, AlertTriangle, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cuttingListSchema, type CuttingListFormData } from '../schemas/quotationSchema';
import {
  PICKUP_TYPE_OPTIONS,
  WAIVED_FREIGHT_OPTIONS,
  QuotationStatus,
  type PickupType,
  type QuotationDraft,
  type WaivedFreight,
} from '../types/quotation.types';
import { FreightChargeSummary } from '@features/freight/components';
import { QuotationLineItemsTable } from './QuotationLineItemsTable';
import { CustomerCombobox } from './CustomerCombobox';
import { TermsCombobox } from './TermsCombobox';
import { ProjectCategoryCombobox } from './ProjectCategoryCombobox';
import { SalesRepCombobox } from './SalesRepCombobox';
import { SalesCoordinatorCombobox } from './SalesCoordinatorCombobox';
import { useQuotationDraftStore } from '../store/quotationDraftStore';
import { quotationIntakeService } from '@features/quotation-intake/services';
import type {
  CustomerOption,
  TermOption,
  ProjectCategoryOption,
  SalesRepOption,
  SalesCoordinatorOption,
  BaseMarkup,
} from '@features/quotation-intake/types/quotationIntake.types';
import { cn } from '@/lib/utils';
import { formatDateTime12h } from '../utils/formatRelative';

interface QuotationComposeFormProps {
  draft: QuotationDraft;
  /** Kept on the prop type for parent-side wiring even though the form no
   *  longer renders its own Submit affordance — the detail panel header owns
   *  the Submit button (CSC-297 cleanup). Reserved for future inline progress
   *  indicators inside the line-items table. */
  isSubmitting?: boolean;
  baseMarkup: BaseMarkup | null;
  /** When true, all inputs are disabled. Applied to submitted quotations. */
  readOnly?: boolean;
  /**
   * Carries the draft id alongside the patch so the parent attributes the change to the right
   * draft even when the user has switched tabs in the 200 ms debounce window. The form captures
   * draft.id at mount; the cleanup flush uses that captured id, not the parent's current activeId.
   */
  onChange: (draftId: string, patch: Partial<QuotationDraft>) => void;
  /** Native form-submit handler. Triggered by RHF's handleSubmit when the user
   *  hits Enter inside an input. The visible submit button lives in the detail
   *  panel header now and calls this same handler via the parent. */
  onSubmit: () => void;
  /** CSC-288 workspace integration. Default 'inline' = legacy split layout
   *  (customer panel left, line items right). 'portal' = customer panel
   *  renders into the provided target element via React.createPortal so the
   *  workspace can host it inside a cabinet-door overlay. RHF state stays
   *  inside this form regardless — only the visual mount point moves. */
  customerPanelMode?: 'inline' | 'portal';
  customerPanelPortalTarget?: HTMLElement | null;
}

export function QuotationComposeForm({
  draft,
  baseMarkup,
  readOnly = false,
  onChange,
  onSubmit,
  customerPanelMode = 'inline',
  customerPanelPortalTarget = null,
}: QuotationComposeFormProps) {
  const customerColRatio = useQuotationDraftStore((s) => s.customerColRatio);
  const setCustomerColRatio = useQuotationDraftStore((s) => s.setCustomerColRatio);
  const customerPanelCollapsed = useQuotationDraftStore((s) => s.customerPanelCollapsed);
  const setCustomerPanelCollapsed = useQuotationDraftStore((s) => s.setCustomerPanelCollapsed);
  const splitRef = useRef<HTMLDivElement>(null);

  const handleDragStart = (e: React.MouseEvent) => {
    e.preventDefault();
    const container = splitRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();

    const onMove = (ev: MouseEvent) => {
      const x = ev.clientX - rect.left;
      setCustomerColRatio(x / rect.width);
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';
  };

  const {
    register,
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<CuttingListFormData>({
    resolver: zodResolver(cuttingListSchema),
    mode: 'onBlur',
    defaultValues: draftToFormData(draft),
  });

  const [terms, setTerms] = useState<TermOption[]>([]);
  const [projectCategories, setProjectCategories] = useState<ProjectCategoryOption[]>([]);
  const [salesReps, setSalesReps] = useState<SalesRepOption[]>([]);
  const [salesCoordinators, setSalesCoordinators] = useState<SalesCoordinatorOption[]>([]);

  useEffect(() => {
    let cancelled = false;
    quotationIntakeService
      .getTerms()
      .then((list) => {
        if (!cancelled) setTerms(list);
      })
      .catch((err) => {
        console.error('Failed to load terms', err);
      });
    quotationIntakeService
      .getProjectCategories()
      .then((list) => {
        if (!cancelled) setProjectCategories(list);
      })
      .catch((err) => {
        console.error('Failed to load project categories', err);
      });
    quotationIntakeService
      .getSalesReps()
      .then((list) => {
        if (!cancelled) setSalesReps(list);
      })
      .catch((err) => {
        console.error('Failed to load sales reps', err);
      });
    quotationIntakeService
      .getSalesCoordinators()
      .then((list) => {
        if (!cancelled) setSalesCoordinators(list);
      })
      .catch((err) => {
        console.error('Failed to load sales coordinators', err);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleCustomerSelect = (opt: CustomerOption | null) => {
    if (opt) {
      const parentName = opt.parentCompanyName ?? opt.companyName;
      const parentId = opt.parentCompanyId ?? opt.internalId;
      const termsId = opt.termsId ?? '';
      const termsName = opt.termsName ?? null;
      setValue('customerName', opt.companyName, { shouldDirty: true, shouldValidate: true });
      setValue('headMainOffice', parentName, { shouldDirty: true, shouldValidate: true });
      setValue('termsId', termsId, { shouldDirty: true, shouldValidate: true });
      setValue('termsName', termsName, { shouldDirty: true });
      onChange(draft.id, {
        customerId: opt.internalId,
        customerEntityId: opt.entityId,
        headMainOfficeCustomerId: parentId,
        termsId: termsId || null,
        termsName,
        salesRepId: opt.salesRepId ?? null,
        salesCoordId: opt.salesCoordId ?? null,
        taxRate: opt.taxRate ?? null,
      });
    } else {
      setValue('customerName', '', { shouldDirty: true, shouldValidate: true });
      setValue('headMainOffice', '', { shouldDirty: true, shouldValidate: true });
      onChange(draft.id, {
        customerId: null,
        customerEntityId: null,
        headMainOfficeCustomerId: null,
        salesRepId: null,
        salesCoordId: null,
        taxRate: null,
      });
    }
  };

  const customerSelection = draft.customerId
    ? { internalId: draft.customerId, companyName: draft.customerName }
    : null;


  useEffect(() => {
    reset(draftToFormData(draft));
  }, [draft.id, reset]);

  // Pick-up YES implies the customer is collecting the goods themselves → freight
  // is structurally waived. Keep the two fields locked in step: whenever pickup
  // transitions to '1' (or the form mounts with pickup already '1' but a stale
  // waivedFreight from older data), force WaivedFreight to '1'. The debounced
  // watch-sync below propagates the change to the draft store; the field itself
  // is rendered disabled while pickup === '1' so the user can't undo it.
  const pickupWatched = watch('pickup');
  const waivedWatched = watch('waivedFreight');
  useEffect(() => {
    if (pickupWatched === '1' && waivedWatched !== '1') {
      setValue('waivedFreight', '1' as WaivedFreight, { shouldDirty: true, shouldValidate: true });
    }
  }, [pickupWatched, waivedWatched, setValue]);

  // Push form changes to the draft store on a short debounce instead of every
  // keystroke. Typing stays local to RHF; the store only sees the fully-settled
  // value after 200 ms of quiet. The cleanup flushes any pending value so draft
  // switches and unmounts don't lose in-progress edits.
  //
  // Capture draft.id when this effect mounts and pass it through `onChange` — when the
  // form re-keys on a draft switch the cleanup runs with this id (not the parent's
  // current activeId), so flushed edits attribute to the draft they came from.
  const draftIdAtMount = draft.id;
  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let latest: Partial<QuotationDraft> | null = null;

    const flush = () => {
      if (latest) {
        onChange(draftIdAtMount, latest);
        latest = null;
      }
      timeoutId = null;
    };

    const sub = watch((value) => {
      latest = value as Partial<QuotationDraft>;
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(flush, 200);
    });

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      flush();
      sub.unsubscribe();
    };
  }, [watch, onChange, draftIdAtMount]);

  // CSC-288 Phase 2: hoist the customer panel Card out of inline JSX so it
  // can be referenced from either the inline slot OR a createPortal call
  // into the workspace's cabinet-door overlay. Only one branch renders to
  // DOM at a time (mode-gated below), so RHF input registration stays
  // single-sourced.
  const customerPanelCard = (
    // In portal mode the Card sits inside the workspace's cabinet-door
    // overlay which already provides bg + border + shadow — collapsing the
    // Card's own chrome avoids the "rounded card inside a sharp-cornered
    // panel" double-container look. Inline mode keeps the legacy chrome.
    <Card
      className={
        customerPanelMode === 'portal'
          ? 'border-0 bg-transparent rounded-none shadow-none'
          : undefined
      }
    >
      <CardHeader className="flex flex-row items-center justify-between gap-2 pb-3 space-y-0">
        <CardTitle className="flex items-center gap-2 text-base">
          <UserRound className="h-4 w-4 text-muted-foreground" />
          Customer Details
        </CardTitle>
        {customerPanelMode === 'inline' && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="hidden h-7 w-7 p-0 text-muted-foreground xl:inline-flex"
            onClick={() => setCustomerPanelCollapsed(true)}
            title="Collapse — give line items full width"
            aria-label="Collapse Customer Details"
          >
            <PanelLeftClose className="h-4 w-4" />
          </Button>
        )}
      </CardHeader>
      <CardContent
        className="space-y-6"
        style={{ containerType: 'inline-size', containerName: 'compose-panel' } as React.CSSProperties}
      >
        {/*
          Fieldset is the cheapest way to lock every form control at once when the
          quotation has been submitted. Native inputs, Radix Select triggers, and
          combobox buttons all respect fieldset[disabled], so we don't have to
          prop-drill `disabled` into every descendant. The `:disabled` pseudo doesn't
          apply to non-form-control elements, so we gate the visual dim via a class.
          We wrap only the grids (not the CardHeader) so the Collapse button at the
          top remains interactive even in read-only mode.
        */}
        <fieldset
          disabled={readOnly}
          className={cn(
            'm-0 min-w-0 space-y-6 border-0 p-0',
            readOnly && 'opacity-80',
          )}
        >
          <div className="cq-grid-3">
            <Field
              label="Customer / Prospect"
              required
              error={errors.customerName?.message}
              className="cq-wrap-narrow"
            >
              <CustomerCombobox
                value={customerSelection}
                onSelect={handleCustomerSelect}
                placeholder="Search synced customers…"
                autoFocus
              />
            </Field>
            <Field
              label="Head / Main Office"
              required
              error={errors.headMainOffice?.message}
              className="cq-wrap-narrow"
            >
              <Input
                {...register('headMainOffice')}
                placeholder="Parent organization"
                title={getValues('headMainOffice') || undefined}
              />
            </Field>
            <Field
              label="Project Category"
              required
              error={errors.projectCategoryId?.message}
            >
              <Controller
                name="projectCategoryId"
                control={control}
                render={({ field }) => (
                  <ProjectCategoryCombobox
                    value={field.value ? { id: field.value, name: draft.projectCategoryName } : null}
                    options={projectCategories}
                    onSelect={(opt) => {
                      const id = opt?.id ?? '';
                      const name = opt?.name ?? null;
                      field.onChange(id);
                      setValue('projectCategoryName', name, { shouldDirty: true });
                      onChange(draft.id, { projectCategoryId: id || null, projectCategoryName: name });
                    }}
                  />
                )}
              />
            </Field>
            <Field
              label="Project"
              required
              error={errors.projectName?.message}
              className="cq-wrap-narrow [&]:col-span-full"
            >
              <Controller
                name="projectName"
                control={control}
                render={({ field }) => (
                  <Textarea
                    rows={2}
                    placeholder="PROJECT NAME"
                    value={field.value ?? ''}
                    onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                    onBlur={field.onBlur}
                    name={field.name}
                    ref={field.ref}
                    title={field.value || undefined}
                    className="resize-none"
                  />
                )}
              />
            </Field>
          </div>

          <SectionHeader title="Terms & Dates" />
          <div className="cq-grid-3">
            <Field label="Terms" required error={errors.termsId?.message}>
              <Controller
                name="termsId"
                control={control}
                render={({ field }) => (
                  <TermsCombobox
                    value={field.value ? { id: field.value, name: draft.termsName } : null}
                    options={terms}
                    onSelect={(opt) => {
                      const id = opt?.id ?? '';
                      const name = opt?.name ?? null;
                      field.onChange(id);
                      setValue('termsName', name, { shouldDirty: true });
                      onChange(draft.id, { termsId: id || null, termsName: name });
                    }}
                  />
                )}
              />
            </Field>
            <Field label="Sales Rep">
              <SalesRepCombobox
                value={
                  draft.salesRepId
                    ? {
                        id: draft.salesRepId,
                        name: salesReps.find((s) => s.id === draft.salesRepId)?.name ?? null,
                      }
                    : null
                }
                options={salesReps}
                onSelect={(opt) => onChange(draft.id, { salesRepId: opt?.id ?? null })}
              />
            </Field>
            <Field label="Sales Coordinator">
              <SalesCoordinatorCombobox
                value={
                  draft.salesCoordId
                    ? {
                        id: draft.salesCoordId,
                        name:
                          salesCoordinators.find((s) => s.id === draft.salesCoordId)?.name ??
                          null,
                      }
                    : null
                }
                options={salesCoordinators}
                onSelect={(opt) => onChange(draft.id, { salesCoordId: opt?.id ?? null })}
              />
            </Field>
            <Field label="Escalation Date" required error={errors.escalationDate?.message}>
              <Input type="date" {...register('escalationDate')} />
            </Field>
            <Field label="Expiration Date" required error={errors.expirationDate?.message}>
              <Input type="date" {...register('expirationDate')} />
            </Field>
          </div>

          <SectionHeader title="Freight" />
          <div className="cq-grid-2">
            <Field label="Pick-up" required error={errors.pickup?.message}>
              <Controller
                name="pickup"
                control={control}
                render={({ field }) => (
                  <Select
                    value={field.value}
                    onValueChange={(v) => field.onChange(v as PickupType)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent>
                      {PICKUP_TYPE_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </Field>
            <Field label="Waived Freight" required error={errors.waivedFreight?.message}>
              <Controller
                name="waivedFreight"
                control={control}
                render={({ field }) => {
                  // Customer pick-up dictates waived — lock the field in that case so
                  // the two can't drift out of sync. The pick-up handler above force-
                  // sets the value to YES when it transitions to '1'.
                  const lockedByPickup = watch('pickup') === '1';
                  return (
                    <Select
                      value={field.value}
                      onValueChange={(v) => field.onChange(v as WaivedFreight)}
                      disabled={lockedByPickup}
                    >
                      <SelectTrigger
                        title={lockedByPickup ? 'Locked: Pick-up is set to Yes (auto-waives freight).' : undefined}
                      >
                        <SelectValue placeholder="Select..." />
                      </SelectTrigger>
                      <SelectContent>
                        {WAIVED_FREIGHT_OPTIONS.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  );
                }}
              />
            </Field>
          </div>
        </fieldset>
      </CardContent>
    </Card>
  );

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="flex flex-1 flex-col gap-4"
      aria-disabled={readOnly}
    >
      {/* CSC-293: flat read-only summary replaces the cabinet/customer panel
          for Submitted/Sent quotations. Inline label:value pairs in a
          responsive grid so the whole header costs ~3 lines of vertical
          space instead of the cabinet's ~720 px overlay. */}
      {readOnly && (
        <>
          <ReadOnlySummaryBand
            draft={draft}
            salesReps={salesReps}
            salesCoordinators={salesCoordinators}
          />
          <FreightChargeSummary draft={draft} />
        </>
      )}
      <div
        ref={splitRef}
        className="flex flex-col items-stretch gap-4 xl:flex-row"
        style={{ '--customer-col': `${(customerColRatio * 100).toFixed(2)}%` } as React.CSSProperties}
      >
        {/* CSC-288: portal mode hides the inline expand/collapse rail. In
            workspace context the cabinet-door pattern lives outside the form
            (top header bar). Phase 2 will move the customer panel itself to
            an overlay via createPortal; today's iteration just toggles the
            collapsed flag from the workspace's button so line items get the
            full width by default.
            CSC-293: in read-only mode the customer panel is replaced by the
            ReadOnlySummaryBand above, so we skip every customer-column
            artifact in the split layout. */}
        {!readOnly && customerPanelMode === 'inline' && customerPanelCollapsed && (
          <button
            type="button"
            onClick={() => setCustomerPanelCollapsed(false)}
            className="group hidden self-stretch xl:flex xl:w-9 xl:shrink-0 xl:flex-col xl:items-center xl:justify-start xl:gap-3 xl:rounded-md xl:border xl:bg-background xl:py-3 xl:text-muted-foreground xl:hover:bg-muted/60 xl:hover:text-foreground"
            title="Expand Customer Details"
            aria-label="Expand Customer Details"
          >
            <PanelLeftOpen className="h-4 w-4" />
            <span className="text-[10px] font-semibold uppercase tracking-widest [writing-mode:vertical-rl]">
              Customer
            </span>
          </button>
        )}

        {!readOnly && customerPanelMode === 'inline' && (
          <div
            className={cn(
              'min-w-0',
              customerPanelCollapsed
                ? 'xl:hidden'
                : 'xl:shrink-0 xl:grow-0 xl:basis-[var(--customer-col)]',
            )}
          >
            {customerPanelCard}
          </div>
        )}

        {!readOnly && customerPanelMode === 'inline' && !customerPanelCollapsed && (
          <div
            className="group hidden shrink-0 cursor-col-resize self-stretch xl:block"
            onMouseDown={handleDragStart}
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize customer / line items columns"
            tabIndex={-1}
            title="Drag to resize"
          >
            <div className="relative h-full w-2">
              <div className="absolute inset-y-0 left-1/2 w-0.5 -translate-x-1/2 rounded-sm bg-border transition-colors group-hover:bg-primary/60 group-active:bg-primary" />
            </div>
          </div>
        )}

        <div className="min-w-0 xl:flex-1">
          {!readOnly && draft.extractionNotice && (
            <ExtractionNoticeBanner draftId={draft.id} notice={draft.extractionNotice} />
          )}
          <QuotationLineItemsTable draft={draft} baseMarkup={baseMarkup} readOnly={readOnly} />
        </div>
      </div>

      {/* CSC-288 Phase 2: portal mode mounts the same customerPanelCard
          into the workspace's cabinet-door overlay. RHF state lives in this
          form, not in the overlay element, so save/dirty/submit semantics
          are unaffected by the visual relocation.
          CSC-293: skip the portal entirely in read-only mode — the cabinet
          isn't rendered (panel hides it) so there's no target, and the flat
          ReadOnlySummaryBand above already presents every customer field. */}
      {!readOnly && customerPanelMode === 'portal' && customerPanelPortalTarget &&
        createPortal(customerPanelCard, customerPanelPortalTarget)}

      {/* CSC-297: cutting-list submission is now driven by the "Create Cutting List"
          button in the detail panel header (with confirmation prompt). The previous
          inline footer + sticky FAB were duplicate affordances that ate vertical
          space and competed with the header button. */}
    </form>
  );
}

function draftToFormData(draft: QuotationDraft): CuttingListFormData {
  return {
    customerName: draft.customerName,
    headMainOffice: draft.headMainOffice,
    projectCategoryId: draft.projectCategoryId ?? '',
    projectCategoryName: draft.projectCategoryName,
    projectName: draft.projectName,
    termsId: draft.termsId ?? '',
    termsName: draft.termsName,
    escalationDate: draft.escalationDate,
    expirationDate: draft.expirationDate,
    pickup: draft.pickup,
    waivedFreight: draft.waivedFreight,
  };
}

// CSC-293: flat read-only summary that replaces the customer-details cabinet
// for Submitted / Sent quotations. Renders submission metadata (cutting-list
// number, posted date, posted by) on the top row and every customer / project
// / freight field as compact label:value pairs in a multi-column grid below.
// No form inputs — saves vertical space and reads at a glance. The component
// receives the salesReps / salesCoordinators arrays loaded by the parent
// QuotationComposeForm so it can resolve the IDs into display names.
function ReadOnlySummaryBand({
  draft,
  salesReps,
  salesCoordinators,
}: {
  draft: QuotationDraft;
  salesReps: SalesRepOption[];
  salesCoordinators: SalesCoordinatorOption[];
}) {
  const salesRepName =
    draft.salesRepId != null
      ? salesReps.find((s) => s.id === draft.salesRepId)?.name ?? null
      : null;
  const salesCoordName =
    draft.salesCoordId != null
      ? salesCoordinators.find((s) => s.id === draft.salesCoordId)?.name ?? null
      : null;
  const pickupLabel =
    PICKUP_TYPE_OPTIONS.find((o) => o.value === draft.pickup)?.label ?? null;
  const waivedFreightLabel =
    WAIVED_FREIGHT_OPTIONS.find((o) => o.value === draft.waivedFreight)?.label ?? null;
  const isSent = draft.status === QuotationStatus.Sent;
  const submittedLabel = isSent ? 'Sent to Client' : 'Submitted to NetSuite';
  const submittedColor = isSent
    ? 'text-purple-700 dark:text-purple-300'
    : 'text-success';

  return (
    <div className="border-y bg-muted/30 px-4 py-3">
      {/* CSC-293: submission detail block split into two rows so the document
          numbers are the visual anchor (top) and the status / posting metadata
          sits as a slimmer subline below. */}
      {(draft.cuttingListNumber || draft.quotationNumber) && (
        <div className="mb-2.5 border-b border-border/60 pb-2.5">
          {/* Top row — NetSuite + DOP document numbers. Both prominent, mono. */}
          <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1">
            {draft.cuttingListNumber && (
              <div className="flex items-baseline gap-1.5">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  NetSuite
                </span>
                {draft.netSuiteUrl ? (
                  <a
                    href={draft.netSuiteUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 font-mono text-sm font-semibold text-info underline decoration-info/40 underline-offset-2 transition-colors hover:decoration-info"
                    title={`Open ${draft.cuttingListNumber} in NetSuite`}
                  >
                    {draft.cuttingListNumber}
                    <ExternalLink className="h-3 w-3" />
                  </a>
                ) : (
                  <span className="font-mono text-sm font-semibold">{draft.cuttingListNumber}</span>
                )}
              </div>
            )}
            {draft.quotationNumber && (
              <div className="flex items-baseline gap-1.5">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  DOP
                </span>
                <span className="font-mono text-sm font-semibold">{draft.quotationNumber}</span>
              </div>
            )}
          </div>
          {/* Second row — status badge + posted-by metadata. Smaller, muted. */}
          <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-0.5 text-xs">
            <span className={cn('inline-flex items-center gap-1 font-semibold uppercase tracking-wide', submittedColor)}>
              <CheckCircle2 className="h-3.5 w-3.5" />
              {submittedLabel}
            </span>
            {draft.postedAt && (
              <span className="text-muted-foreground tabular-nums">
                <span className="font-medium">Posted:</span> {formatDateTime12h(draft.postedAt)}
              </span>
            )}
            {draft.postedByName && (
              <span className="text-muted-foreground">
                <span className="font-medium">By:</span> {draft.postedByName}
              </span>
            )}
          </div>
        </div>
      )}
      {/* Customer / project / dates / freight — compact label:value grid. */}
      <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-sm md:grid-cols-3 lg:grid-cols-4">
        <ReadOnlyPair label="Customer" value={draft.customerName} />
        <ReadOnlyPair label="Head Office" value={draft.headMainOffice} />
        <ReadOnlyPair label="Project Cat." value={draft.projectCategoryName} />
        <ReadOnlyPair label="Project" value={draft.projectName} className="md:col-span-3 lg:col-span-4" />
        <ReadOnlyPair label="Terms" value={draft.termsName} />
        <ReadOnlyPair label="Sales Rep" value={salesRepName} />
        <ReadOnlyPair label="Sales Coord." value={salesCoordName} />
        <ReadOnlyPair label="Date" value={draft.date} />
        <ReadOnlyPair label="Escalation" value={draft.escalationDate} />
        <ReadOnlyPair label="Expiration" value={draft.expirationDate} />
        <ReadOnlyPair label="Pick-up" value={pickupLabel} />
        <ReadOnlyPair label="Waived Freight" value={waivedFreightLabel} />
      </div>
    </div>
  );
}

function ReadOnlyPair({
  label,
  value,
  className,
}: {
  label: string;
  value: string | null | undefined;
  className?: string;
}) {
  const display = value && value.trim().length > 0 ? value : '—';
  return (
    <div className={cn('flex min-w-0 items-baseline gap-1.5', className)}>
      <span className="shrink-0 text-xs text-muted-foreground">{label}:</span>
      <span className="min-w-0 truncate font-medium" title={display}>
        {display}
      </span>
    </div>
  );
}


function SectionHeader({ title, subtitle }: { title: string; subtitle?: string }) {
  return (
    <div className="border-b pb-1.5">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
        {title}
      </h2>
      {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
    </div>
  );
}

interface ExtractionNoticeBannerProps {
  draftId: string;
  notice: NonNullable<QuotationDraft['extractionNotice']>;
}

function ExtractionNoticeBanner({ draftId, notice }: ExtractionNoticeBannerProps) {
  const dismiss = useQuotationDraftStore((s) => s.dismissExtractionNotice);
  let message: string;
  if (notice.kind === 'all-defaulted') {
    message = `No quantities were specified — defaulted all ${notice.count} item${notice.count === 1 ? '' : 's'} to 1 pc each. Please review and update before submitting.`;
  } else if (notice.kind === 'partial-dropped') {
    message = `${notice.count} item${notice.count === 1 ? '' : 's'} were missing quantity and were skipped. The remaining items were processed normally.`;
  } else if (notice.kind === 'kg-dropped') {
    // 14ykpqtw3hw QUO-R-003 — say WHY each kg line is not a row, so nobody hunts for a lost order.
    message = kgDroppedMessage(notice);
  } else {
    // CSC-291: grades-promoted variant. Render a comma-separated summary of the
    // (from→to, count) pairs so sales sees exactly which grades changed and how many.
    const total = notice.promotions.reduce((sum, p) => sum + p.count, 0);
    const detail = notice.promotions
      .map((p) => `G${p.from}→G${p.to} (${p.count})`)
      .join(' · ');
    message = `${total} line${total === 1 ? '' : 's'} were auto-promoted to higher grades per CSC policy. ${detail}.`;
  }
  return (
    <div className="flex items-start gap-2 border-y border-warning/30 bg-warning/10 p-3 text-xs text-warning">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
      <p className="flex-1">{message}</p>
      <button
        type="button"
        onClick={() => dismiss(draftId)}
        className="shrink-0 rounded p-0.5 text-warning hover:bg-warning/15 hover:text-warning"
        aria-label="Dismiss notice"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

function kgDroppedMessage(notice: { repeats: number; totals: number; figures: number }): string {
  const lines = (n: number) => `${n} kg line${n === 1 ? '' : 's'}`;
  const parts: string[] = [];
  if (notice.repeats > 0) parts.push(`${lines(notice.repeats)} dropped — same bars already listed in pieces`);
  if (notice.totals > 0) parts.push(`${lines(notice.totals)} dropped — the total of the other lines`);
  if (notice.figures > 0)
    parts.push(`${lines(notice.figures)} left out — a weight with no size and length isn't quoted as pieces`);
  return `${parts.join('; ')}.`;
}

interface FieldProps {
  label: string;
  required?: boolean;
  error?: string;
  className?: string;
  children: React.ReactNode;
}

function Field({ label, required, error, className, children }: FieldProps) {
  return (
    <div className={cn('space-y-1.5', className)}>
      <Label className="text-xs font-medium">
        {label}
        {required && <span className="ml-0.5 text-destructive">*</span>}
      </Label>
      {children}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}
